import type { TypedDocumentNode } from '@apollo/client';
import { gql } from '@apollo/client';
import classNames from 'classnames';
import { type CSSProperties } from 'react';
import { FormattedMessage } from 'react-intl';
import styled from 'styled-components';

import { Link } from '@sorare/routing';
import { IconButton } from '@sorare/core/src/atoms/buttons/IconButton';
import { FontAwesomeIcon } from '@sorare/core/src/atoms/icons';
import { iconChevronRight } from '@sorare/core/src/atoms/icons/system/iconChevronRight.ts';
import { SBHorizontal, Vertical } from '@sorare/core/src/atoms/layout/flex';
import { LabelL } from '@sorare/core/src/atoms/typography';
import { Score } from '@sorare/core/src/components/collections/Score';
import { SplitProgressBar } from '@sorare/core/src/components/collections/SplitProgressBar';
import { OnboardingHighlightWrapper } from '@sorare/core/src/components/onboarding/OnboardingHighlightWrapper';
import { ClickMarketHomeSection_Target } from '@sorare/core/src/protos/events/so5/shared/events';

import { SlotsPreview } from '#src/components/collections/SlotsPreview/index.tsx';

import { CollectionGradientWrapper } from './CollectionGradientWrapper/index.tsx';
import { CollectionPreviewHeader } from './CollectionPreviewHeader/index.tsx';
import { CollectionPreviewStats } from './CollectionPreviewStats/index.tsx';
import { EmptyCollection } from './EmptyCollection/index.tsx';
import { Shimmer } from './Shimmer/index.tsx';
import type { CollectionPreview_cardCollection } from './__generated__/index.graphql.ts';
import { useCardCollectionData } from './useCardCollectionData.ts';

const Root = styled(Vertical).attrs({ gap: 2 })`
  justify-content: space-between;
  height: 100%;
  border-radius: var(--double-unit);
  background-color: var(--c-black);
`;

const WhiteColorWrapper = styled.span`
  font-weight: var(--t-bold);
  color: var(--c-white);
`;

const SlotsWrapper = styled.div``;

const Footer = styled(Vertical).attrs({ gap: 0.5 })`
  width: 100%;
  padding: 0 var(--double-unit) var(--double-unit);
`;

const BaselineLabelL = styled(LabelL)`
  display: inline-flex;
  align-items: center;
  gap: var(--unit);
`;

type Props = {
  collection: CollectionPreview_cardCollection;
  to?: string;
  shouldBeHighlighted?: boolean;
  withStats?: boolean;
  track?: (p: {
    target: ClickMarketHomeSection_Target;
    collectionSlug?: string;
  }) => void;
};

export const CollectionPreview = ({
  collection,
  to,
  shouldBeHighlighted,
  withStats = false,
  track,
}: Props) => {
  const { userCardCollection, rarity, team, bonusThresholds, snapshotted } =
    collection;

  const { hexColor } = useCardCollectionData(collection);

  const { fulfilledSlotsCount } = userCardCollection || {};
  if (!team) return null;

  const nextThreshold = bonusThresholds.find(({ threshold }) => {
    return threshold > (userCardCollection?.score || 0);
  })?.threshold;

  const emptyCollection = !fulfilledSlotsCount;
  const { complete } = userCardCollection || {};

  if (emptyCollection) {
    return <EmptyCollection cardCollection={collection} to={to} />;
  }

  return (
    <CollectionGradientWrapper
      className={classNames({ complete, hoverable: !!to })}
      style={
        {
          '--completed-color': hexColor,
        } as CSSProperties
      }
    >
      <Root
        as={to ? Link : 'div'}
        to={to}
        onClick={() =>
          track?.({
            target: ClickMarketHomeSection_Target.COLLECTION_PAGE,
            collectionSlug: collection.slug,
          })
        }
      >
        <CollectionPreviewHeader
          collection={collection}
          shouldBeHighlighted={shouldBeHighlighted}
        />
        <SlotsWrapper>
          <SlotsPreview
            cardCollection={collection}
            shouldBeHighlighted={shouldBeHighlighted}
          />
        </SlotsWrapper>
        <Footer>
          {!snapshotted && !!nextThreshold && (
            <SBHorizontal>
              <BaselineLabelL color="var(--c-nd-600)">
                <FormattedMessage
                  defaultMessage="{score} to the next level"
                  values={{
                    score: (
                      <WhiteColorWrapper>
                        <Score
                          score={
                            nextThreshold - (userCardCollection?.score || 0)
                          }
                          rarity={rarity}
                        />
                      </WhiteColorWrapper>
                    ),
                  }}
                />
              </BaselineLabelL>
              <IconButton color="secondary" size="compact">
                <FontAwesomeIcon icon={iconChevronRight} size="sm" />
              </IconButton>
            </SBHorizontal>
          )}
          <OnboardingHighlightWrapper
            highlightName="CollectionPreviewCollectionBonusProgressBar"
            wrap={!!shouldBeHighlighted}
          >
            {!!bonusThresholds.length && !snapshotted && (
              <SplitProgressBar
                cardCollection={collection}
                userCardCollection={collection.userCardCollection}
              />
            )}
          </OnboardingHighlightWrapper>
          {!!withStats && (
            <CollectionPreviewStats
              userCardCollection={userCardCollection}
              shouldBeHighlighted={shouldBeHighlighted}
              snapshotted={snapshotted}
            />
          )}
        </Footer>
        {!!complete && <Shimmer rarity={rarity} className="visible" />}
      </Root>
    </CollectionGradientWrapper>
  );
};

CollectionPreview.fragments = {
  cardCollection: gql`
    fragment CollectionPreview_cardCollection on CardCollection {
      slug
      rarity
      snapshotted
      bonusThresholds {
        threshold
      }
      team: anyTeam {
        slug
        name
        pictureUrl
      }
      userCardCollection(forUserSlug: $user) {
        id
        fulfilledSlotsCount
        complete
        score
        ...SplitProgressBar_userCardCollection
        ...CollectionPreviewStats_userCardCollection
      }
      ...CollectionPreviewHeader_cardCollection
      ...useCardCollectionData_cardCollection
      ...SplitProgressBar_cardCollection
      ...SlotsPreview_cardCollection
      ...EmptyCollection_cardCollection
    }
    ${CollectionPreviewHeader.fragments.cardCollection}
    ${useCardCollectionData.fragments.cardCollection}
    ${SplitProgressBar.fragments.cardCollection}
    ${SplitProgressBar.fragments.userCardCollection}
    ${CollectionPreviewStats.fragments.userCardCollection}
    ${SlotsPreview.fragments.cardCollection}
    ${EmptyCollection.fragments.cardCollection}
  ` as TypedDocumentNode<CollectionPreview_cardCollection>,
};
